import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { LogoLoader } from '@/components/ui/logo-loader';

import { CredentialNotificationRetry } from './credential-notification-retry';
import { CredentialRequestOverlay } from './credential-request-overlay';
import {
  type SavedCredentialRequest as Request,
  useSavedCredentialActions,
} from './saved-credential.query';

export const SavedCredentialRequest = ({
  request,
  onBeforeSubmit,
  overlay = false,
}: {
  request: Request;
  onBeforeSubmit?: () => void | Promise<void>;
  overlay?: boolean;
}) => {
  const [submitted, setSubmitted] = useState(false);
  const actions = useSavedCredentialActions(request);
  const pending = request.status === 'pending';
  const showChoices =
    pending && (!submitted || actions.isError) && !actions.isPending;

  const send = async (
    body:
      | { action: 'approve' | 'reject' }
      | { action: 'choose'; section_id: string; choice_id: string }
  ) => {
    setSubmitted(true);
    await actions.submit(body, onBeforeSubmit);
  };

  if (overlay && request.status === 'resolved' && !request.notified) {
    return (
      <CredentialNotificationRetry
        pending={actions.isPending}
        isError={actions.isError}
        onRetry={() => void actions.resume()}
      />
    );
  }
  if (overlay && !showChoices) return null;

  const content = (
    <YStack
      className="gap-4 rounded-lg border border-border bg-surface p-4 text-foreground"
      data-testid="saved-credential-request"
    >
      {showChoices ? (
        request.sections.length ? (
          <>
            {request.sections.map((section) => (
              <YStack
                key={section.id}
                role="group"
                aria-label={section.label}
                className="gap-2"
              >
                <h2 className="text-base font-medium">{section.label}</h2>
                {section.choices.map((choice) => (
                  <Button
                    key={choice.id}
                    variant="secondary"
                    className="h-auto min-h-9 w-full [&>span]:w-full [&>span]:justify-start [&>span]:break-words [&>span]:text-left [&>span]:whitespace-normal"
                    onClick={() =>
                      void send({
                        action: 'choose',
                        section_id: section.id,
                        choice_id: choice.id,
                      })
                    }
                  >
                    {choice.label}
                  </Button>
                ))}
              </YStack>
            ))}
            <Button
              variant="ghost"
              onClick={() => void send({ action: 'reject' })}
            >
              Reject
            </Button>
          </>
        ) : (
          <>
            <h2 className="text-base font-medium">Use saved sign-in?</h2>
            <XStack className="justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => void send({ action: 'reject' })}
              >
                Reject
              </Button>
              <Button
                className="min-w-0 h-auto min-h-9 [&>span]:w-full [&>span]:justify-start [&>span]:break-all [&>span]:text-left [&>span]:whitespace-normal"
                onClick={() => void send({ action: 'approve' })}
              >
                {request.credential_id}
              </Button>
            </XStack>
          </>
        )
      ) : (
        <YStack center className="gap-3 py-5 text-center">
          {request.status !== 'expired' ? (
            <YStack aria-hidden="true" className="size-8">
              <LogoLoader activity="think" flightOnly />
            </YStack>
          ) : null}
          <p role="status" className="text-sm text-foreground-muted">
            {request.status === 'expired'
              ? 'Request expired.'
              : request.status === 'resolved' && !request.notified
                ? 'The agent hasn’t received your response yet.'
                : actions.isPending || (submitted && pending)
                  ? 'Processing your response…'
                  : 'Working on the next step…'}
          </p>
        </YStack>
      )}
      {actions.isError ? (
        <p role="alert" className="text-sm text-danger-11">
          Request failed. Please try again.
        </p>
      ) : null}
      {request.status === 'resolved' && !request.notified ? (
        <Button
          disabled={actions.isPending}
          onClick={() => void actions.resume()}
        >
          Retry
        </Button>
      ) : null}
    </YStack>
  );

  return overlay ? (
    <CredentialRequestOverlay label="Saved credential request">
      {content}
    </CredentialRequestOverlay>
  ) : (
    content
  );
};
